import type { ProjectDTO } from '@pace/contracts';
import type { ProjectStatus, StepStatus } from '@pace/domain';
import { Chip, type ChipTone } from '@pace/ui';
import type { IconName } from '@pace/ui';
import { useI18n } from '../i18n';

export const STEP_TONE: Record<StepStatus, { tone: ChipTone; icon: IconName | null }> = {
  DONE: { tone: 'ok', icon: 'check' },
  DONE_LATE: { tone: 'ok', icon: 'check' },
  OVERDUE: { tone: 'hold', icon: 'warn' },
  DUE_TODAY: { tone: 'wait', icon: 'clock' },
  AT_RISK: { tone: 'wait', icon: 'warn' },
  UPCOMING: { tone: 'neutral', icon: 'calendar' },
  HOLD: { tone: 'hold', icon: 'stop' },
  SKIPPED: { tone: 'neutral', icon: null },
};

export const PROJECT_TONE: Record<ProjectStatus, { tone: ChipTone; icon: IconName }> = {
  ON_TRACK: { tone: 'ok', icon: 'check' },
  AT_RISK: { tone: 'wait', icon: 'warn' },
  LATE: { tone: 'hold', icon: 'clock' },
  SHIPPED: { tone: 'info', icon: 'truck' },
};

/** CSS modifier for a status cell (grid, display): ok / wait / hold / none. */
export function cellTone(s: StepStatus): 'ok' | 'late' | 'wait' | 'hold' | 'none' | 'na' {
  if (s === 'DONE') return 'ok';
  if (s === 'DONE_LATE') return 'late';
  if (s === 'OVERDUE' || s === 'HOLD') return 'hold';
  if (s === 'DUE_TODAY' || s === 'AT_RISK') return 'wait';
  if (s === 'SKIPPED') return 'na';
  return 'none';
}

export function StepChip({ status, slip }: { status: StepStatus; slip?: number }) {
  const { t } = useI18n();
  const { tone, icon } = STEP_TONE[status];
  return (
    <Chip tone={tone} icon={icon}>
      {t(`status.${status}`)}
      {slip && slip > 0 && status !== 'UPCOMING' ? ` ${t('slip.late', { n: slip })}` : ''}
    </Chip>
  );
}

export function ProjectChip({ status, phase }: { status: ProjectStatus | null; phase: ProjectDTO['phase'] }) {
  const { t } = useI18n();
  if (phase === 'draft')
    return (
      <Chip tone="neutral" icon="doc">
        {t('pstatus.DRAFT')}
      </Chip>
    );
  if (phase === 'cancelled')
    return (
      <Chip tone="CANCELLED" icon="x">
        {t('pstatus.CANCELLED')}
      </Chip>
    );
  if (!status) return null;
  const { tone, icon } = PROJECT_TONE[status];
  return (
    <Chip tone={tone} icon={icon}>
      {t(`pstatus.${status}`)}
    </Chip>
  );
}

/** "+3d" late (red), "−1d" early (green), nothing when on time. */
export function Slip({ days, long }: { days: number; long?: boolean }) {
  const { t } = useI18n();
  if (!days) return null;
  const late = days > 0;
  const n = Math.abs(days);
  return (
    <span className={`slip slip--${late ? 'late' : 'early'}`}>
      {long
        ? t(late ? 'slip.lateLong' : 'slip.earlyLong', { n })
        : t(late ? 'slip.late' : 'slip.early', { n })}
    </span>
  );
}
